import { useEffect, useState } from "react";
import { LogOut, Store, ClipboardList, Users, ExternalLink, KeyRound, BarChart3, LayoutDashboard, Contact, ShieldCheck, UserSearch, ReceiptText, Image as ImageIcon, FlaskConical, Router } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth";
import { api, errMsg, storeLogoUrl } from "@/api";
import { LogoPanel } from "@/components/LogoPanel";
import { VoucherEditor } from "@/components/VoucherEditor";
import { OrderList } from "@/components/OrderList_gen3";
import { DateFilter, rangeFor } from "@/components/DateFilter";
import { StoreManager } from "@/components/StoreManager_gen2";
import { ReportPanel } from "@/components/ReportPanel";
import { EbillingCustomers } from "@/components/EbillingCustomers";
import { LaporanBeta } from "@/components/LaporanBeta";
import { SummaryPanel } from "@/components/SummaryPanel";
import { PasswordDialog } from "@/components/Dialogs";
import { ResellerManager } from "@/components/ResellerManager";
import { MikrotikPanel } from "@/components/MikrotikPanel";
import { Link } from "react-router-dom";

// mode: "super" (/superadmin) | "store" (/toko/:slug/admin)
export default function AdminPage({ mode = "store" }) {
  const { user, logout } = useAuth();
  const isSuper = mode === "super" && user.role === "superadmin";
  const [tab, setTab] = useState("voucher");
  const [stores, setStores] = useState([]);
  const [mainSlug, setMainSlug] = useState("");
  const [managed, setManaged] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dateSel, setDateSel] = useState({ preset: "all", custom: {} });
  const range = rangeFor(dateSel.preset, dateSel.custom);

  const loadStores = async () => {
    if (!isSuper) return;
    const r = await api.get("/admin/stores");
    setStores(r.data);
  };

  useEffect(() => {
    api.get("/admin/store").then((r) => setManaged(r.data));
    api.get("/public/main-slug").then((r) => setMainSlug(r.data.slug));
    loadStores();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeOwnPassword = async (pw) => {
    try { await api.put("/admin/me/password", { password: pw }); toast.success("Password Anda berhasil diganti"); }
    catch (e) { throw new Error(errMsg(e)); }
  };

  const bump = () => { setRefreshKey((k) => k + 1); loadStores(); };
  const manage = (s) => { setManaged(s); setTab("voucher"); toast.info(`Sekarang mengelola: ${s.store_name}`); };
  const storeId = isSuper && managed ? managed.id : undefined;
  const reloadManaged = async () => {
    const r = await api.get("/admin/store", { params: storeId ? { store_id: storeId } : {} });
    setManaged(r.data);
  };

  // Hak akses: rentang waktu laporan hanya tampil bila diaktifkan superadmin
  const canRange = isSuper || managed?.access_rights?.rentang_waktu_laporan !== false;

  // Banner status trial/langganan (display saja — enforcement tetap di backend)
  const subInfo = (() => {
    if (!managed) return null;
    if (managed.subscription_status === "suspended") return { cls: "sub-banner locked", text: "AKUN TERKUNCI — toko dinonaktifkan oleh administrator. Hubungi penyedia layanan.", tid: "sub-banner-locked" };
    if (managed.subscription_status === "trial" && managed.trial_due_at) {
      const ms = new Date(managed.trial_due_at) - new Date();
      if (ms <= 0) return { cls: "sub-banner locked", text: `TRIAL BERAKHIR pada ${new Date(managed.trial_due_at).toLocaleString("id-ID")} — akun terkunci. Hubungi administrator untuk berlangganan.`, tid: "sub-banner-expired" };
      const h = Math.floor(ms / 3600000);
      return { cls: "sub-banner trial", text: `TRIAL AKTIF — Sisa Trial: ${Math.floor(h / 24)} hari ${h % 24} jam (berakhir ${new Date(managed.trial_due_at).toLocaleString("id-ID")})`, tid: "sub-banner-trial" };
    }
    if (managed.due_date) {
      const ms = new Date(managed.due_date + "T23:59:59") - new Date();
      const plan = managed.billing_plan === "yearly" ? "Tahunan" : "Bulanan";
      if (ms <= 0) return { cls: "sub-banner locked", text: `SUBSCRIPTION EXPIRED (${plan}, berakhir ${new Date(managed.due_date).toLocaleDateString("id-ID")}) — akun terkunci. Hubungi administrator.`, tid: "sub-banner-expired" };
      return { cls: "sub-banner active", text: `SUBSCRIPTION AKTIF (${plan}) — Berlaku sampai: ${new Date(managed.due_date).toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" })}`, tid: "sub-banner-active" };
    }
    return null;
  })();

  // Menu digabung per konteks (tanpa menghapus tab/komponen lama; id tab tetap)
  const groups = [
    { id: "produk", label: "Produk", icon: Store, tabs: [{ id: "voucher", label: "Produk Jasa & Aplikasi", icon: Store }] },
    { id: "mitra", label: "Mitra", icon: Contact, tabs: [{ id: "resellers", label: "Reseller & PIN", icon: Contact }] },
    { id: "pesanan", label: "Pesanan", icon: ClipboardList, tabs: [{ id: "orders", label: "Pesanan", icon: ClipboardList }] },
    { id: "laporan", label: "Laporan", icon: BarChart3, tabs: [
      { id: "report_beta", label: "Laporan Baru", icon: FlaskConical },
      { id: "report", label: "Laporan", icon: BarChart3 },
    ] },
    { id: "ebilling", label: "e-Billing", icon: UserSearch, tabs: [
      { id: "pelanggan", label: "Pelanggan", icon: UserSearch },
      { id: "tagihan", label: "Tagihan", icon: ReceiptText },
    ] },
    { id: "pengaturan", label: "Pengaturan Toko", icon: ImageIcon, tabs: [{ id: "logo", label: "Logo Toko", icon: ImageIcon }, { id: "mikrotik", label: "Koneksi MikroTik", icon: Router }] },
    ...(isSuper ? [{ id: "superadmin", label: "Superadmin", icon: ShieldCheck, tabs: [
      { id: "summary", label: "Ringkasan Semua Toko", icon: LayoutDashboard },
      { id: "stores", label: "Admin & Toko", icon: Users },
    ] }] : []),
  ];
  const activeGroup = groups.find((g) => g.tabs.some((t) => t.id === tab)) || groups[0];

  return (
    <div className={`app-shell admin-shell ${isSuper ? "super-shell" : ""}`}>
      <div className="container">
        <header className="app-header">
          <div>
            <h1 className="app-title">{isSuper ? <><ShieldCheck size={26} className="title-icon" /> Panel Admin Utama</> : "Panel Admin Toko"}</h1>
            <p className="app-subtitle" data-testid="admin-user">{user.username} · {isSuper ? "Superadmin — mengelola semua toko" : "Admin Toko"}</p>
          </div>
          <div className="header-actions">
            <button className="btn-ghost sm" onClick={() => setPwOpen(true)} data-testid="change-own-password"><KeyRound size={14} /> Password</button>
            <button className="btn-ghost sm" onClick={logout} data-testid="logout-btn"><LogOut size={14} /> Keluar</button>
          </div>
        </header>

        {managed && (
          <div className="managed-bar" data-testid="managed-store-bar">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {managed.logo_path
                ? <img src={storeLogoUrl(managed)} alt={`Logo ${managed.store_name}`} className="store-logo" style={{ width: 40, height: 40 }} data-testid="managed-store-logo" />
                : <div className="store-logo store-logo-default" style={{ width: 40, height: 40, fontSize: 18 }} data-testid="managed-store-logo-default">{(managed.store_name || "?").trim().charAt(0).toUpperCase()}</div>}
              <div>
                <span className="muted-sm">Toko yang dikelola</span>
                <div className="managed-name" data-testid="managed-store-name">{managed.store_name}</div>
              </div>
            </div>
            <Link to={`/toko/${managed.slug}`} target="_blank" className="link-btn" data-testid="managed-store-link">
              Buka halaman toko <ExternalLink size={13} />
            </Link>
          </div>
        )}

        {subInfo && (
          <div className={subInfo.cls} data-testid={subInfo.tid} style={{ margin: "8px 0", padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: subInfo.cls.includes("locked") ? "#fee2e2" : subInfo.cls.includes("trial") ? "#ede9fe" : "#dcfce7",
            color: subInfo.cls.includes("locked") ? "#b91c1c" : subInfo.cls.includes("trial") ? "#6d28d9" : "#166534" }}>
            {subInfo.text}
          </div>
        )}

        <nav className="tabs" data-testid="admin-tabs">
          {groups.map((g) => {
            const single = g.tabs.length === 1;
            return (
              <button key={g.id} className={`tab ${activeGroup.id === g.id ? "active" : ""}`} onClick={() => setTab(g.tabs[0].id)}
                data-testid={single ? `tab-${g.tabs[0].id}` : `tabgroup-${g.id}`}>
                <g.icon size={15} /> {g.label}
              </button>
            );
          })}
        </nav>
        {activeGroup.tabs.length > 1 && (
          <nav className="tabs" data-testid={`subtabs-${activeGroup.id}`} style={{ marginTop: -4 }}>
            {activeGroup.tabs.map((t) => (
              <button key={t.id} className={`tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </nav>
        )}

        {["orders", "report", "summary"].includes(tab) && (canRange
          ? <DateFilter value={dateSel} onChange={setDateSel} />
          : <p className="muted-sm" data-testid="range-disabled-note">Fitur Rentang Waktu Laporan dinonaktifkan oleh superadmin.</p>)}

        <div className="panel">
          {tab === "voucher" && managed && <VoucherEditor key={managed.id} storeId={storeId} onSaved={bump} />}
          {tab === "resellers" && managed && <ResellerManager key={managed.id} storeId={storeId} store={managed} />}
          {tab === "orders" && managed && <OrderList key={managed.id} storeId={storeId} range={canRange ? range : undefined} onChanged={bump} store={managed} />}
          {tab === "report" && managed && <ReportPanel key={managed.id} storeId={storeId} refreshKey={refreshKey} range={canRange ? range : undefined} store={managed} />}
          {tab === "report_beta" && managed && <LaporanBeta key={managed.id + "-beta"} storeId={storeId} store={managed} />}
          {tab === "pelanggan" && managed && <EbillingCustomers key={managed.id + "-p"} storeId={storeId} store={managed} mode="pelanggan" />}
          {tab === "tagihan" && managed && <EbillingCustomers key={managed.id + "-t"} storeId={storeId} store={managed} mode="tagihan" />}
          {tab === "logo" && managed && <LogoPanel key={managed.id} store={managed} readOnly={isSuper && managed.slug !== mainSlug} onSaved={reloadManaged} />}
          {tab === "mikrotik" && managed && <MikrotikPanel key={managed.id + "-mt"} storeId={storeId} />}
          {tab === "summary" && isSuper && <SummaryPanel refreshKey={refreshKey} onManage={manage} range={range} />}
          {tab === "stores" && isSuper && <StoreManager stores={stores} reload={loadStores} mainSlug={mainSlug} onManage={manage} />}
        </div>
      </div>

      <PasswordDialog
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        title="Ganti Password Saya"
        description="Gunakan password yang kuat dan jangan bagikan ke orang lain."
        onSubmit={changeOwnPassword}
        testId="own-pw-dialog"
      />
    </div>
  );
}
